import React from 'react';
import { Select } from '@cimpress/react-components';

export interface CoamResources {
    fulfillers?: string[];
    merchants?: string[];
}

interface Props {
    type?: string;
    id?: string;
    resources?: CoamResources;
    onChange: (type: string, id: string) => void;
}

// Builds the dropdown options for one resource type (e.g. all fulfiller ids), skipping the '*'
// wildcard entry. `resourceType` is a `keyof CoamResources` rather than a hardcoded literal union
// so this keeps matching `CoamResources`'s own keys if that type ever changes.
//
// `resourceIds` accepts `null` in addition to `CoamResources`'s own `string[] | undefined` fields --
// a default parameter alone wouldn't be enough, since defaults only apply for `undefined`, not
// `null`. Nothing currently sends `null` here (coamClient.ts's mapCoamResponse omits a key entirely
// rather than setting it to `null`), but a `.filter` call on a `null` value would otherwise throw,
// and this is a presentational component with no control over what a future caller passes in.
const buildOptions = (resourceType: keyof CoamResources, resourceIds?: string[] | null) =>
    (resourceIds ?? [])
        .filter((resourceId) => resourceId !== '*')
        .map((resourceId) => ({
            value: `${resourceType},${resourceId}`,
            label: resourceId,
        }));

const CoamDropdown: React.FC<Props> = ({ type, id, resources, onChange }) => {
    let values: string[] | { value: string; label: string }[] = ['Loading...'];
    let selectedValue = 'Loading...';

    if (resources) {
        selectedValue = `${type},${id}`;
        values = [
            ...buildOptions('fulfillers', resources.fulfillers),
            ...buildOptions('merchants', resources.merchants),
        ];
    }

    const onSelect = (option: { value: string }) => {
        // Don't cause a UI change if the selected option is the same
        if (option.value !== selectedValue) {
            const [resourceType, resourceId] = option.value.split(',');
            onChange(resourceType, resourceId);
        }
    };

    return (
        <Select
            label='Tenant'
            classNamePrefix='tenant'
            className='coam__dropdown'
            options={values}
            value={{ label: selectedValue.split(',')[1], value: selectedValue }}
            onChange={onSelect}
        />
    );
};

export default React.memo(CoamDropdown);
